import React, { useState, useEffect, useCallback } from "react";
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
  ScatterChart,
  Scatter,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/Card";
import { Button } from "../ui/Button";
import {
  TrendingUp,
  TrendingDown,
  Users,
  Activity,
  DollarSign,
  Clock,
  MapPin,
  Zap,
  Shield,
  Target,
  RefreshCw,
  AlertCircle,
} from "lucide-react";
import superAdminAnalyticsService from "../../services/superAdminAnalyticsService";

const SuperAdminPlatformChart = () => {
  const [selectedMetric, setSelectedMetric] = useState("revenue");
  const [timeRange, setTimeRange] = useState("6months");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [chartKey, setChartKey] = useState(0);

  // Dynamic data states
  const [platformOverviewData, setPlatformOverviewData] = useState([]);
  const [userGrowthData, setUserGrowthData] = useState([]);
  const [revenueBreakdownData, setRevenueBreakdownData] = useState([]);
  const [geographicData, setGeographicData] = useState([]);
  const [systemPerformanceData, setSystemPerformanceData] = useState([]);
  const [hospitalPerformanceData, setHospitalPerformanceData] = useState([]);
  const [driverPerformanceData, setDriverPerformanceData] = useState([]);

  // Summary data states
  const [platformSummary, setPlatformSummary] = useState({});
  const [userGrowthSummary, setUserGrowthSummary] = useState({});
  const [revenueSummary, setRevenueSummary] = useState({});
  const [performanceSummary, setPerformanceSummary] = useState({});
  const [geographicSummary, setGeographicSummary] = useState({});

  // Data fetching functions
  const fetchAnalyticsData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Clear existing data first to force re-render
      switch (selectedMetric) {
        case "overview":
          setPlatformOverviewData([]);
          break;
        case "users":
          setUserGrowthData([]);
          break;
        case "revenue":
          setRevenueBreakdownData([]);
          setHospitalPerformanceData([]);
          break;
        case "performance":
          setDriverPerformanceData([]);
          setSystemPerformanceData([]);
          break;
        case "geographic":
          setGeographicData([]);
          break;
        default:
          break;
      }

      const params = {
        timeRange,
        ...superAdminAnalyticsService.formatDateRange(timeRange),
      };

      console.log("🔄 Fetching analytics:", {
        metric: selectedMetric,
        timeRange,
        params,
        timestamp: new Date().toISOString(),
      });

      const data = await superAdminAnalyticsService.getAnalyticsByMetric(
        selectedMetric,
        params
      );

      console.log("✅ Analytics data received:", {
        metric: selectedMetric,
        timeRange,
        data: data.data,
        timestamp: new Date().toISOString(),
      });

      switch (selectedMetric) {
        case "overview":
          console.log(
            "📊 Setting Platform Overview Data:",
            data.data.chartData
          );
          setPlatformOverviewData(data.data.chartData || []);
          setPlatformSummary(data.data.summary || {});
          setChartKey((prev) => prev + 1); // Force chart re-render
          break;
        case "users":
          console.log("👥 Setting User Growth Data:", data.data.chartData);
          console.log("Chart Data Length:", data.data.chartData?.length);
          console.log("First Item:", data.data.chartData?.[0]);
          console.log(
            "Last Item:",
            data.data.chartData?.[data.data.chartData?.length - 1]
          );
          setUserGrowthData(data.data.chartData || []);
          setUserGrowthSummary(data.data.summary || {});
          setChartKey((prev) => prev + 1); // Force chart re-render
          break;
        case "revenue":
          setRevenueBreakdownData(data.data.breakdownData || []);
          setHospitalPerformanceData(data.data.hospitalPerformance || []);
          setRevenueSummary(data.data.summary || {});
          setChartKey((prev) => prev + 1); // Force chart re-render
          break;
        case "performance":
          setDriverPerformanceData(data.data.driverPerformance || []);
          setSystemPerformanceData(data.data.systemMetrics || []);
          setPerformanceSummary(data.data.summary || {});
          setChartKey((prev) => prev + 1); // Force chart re-render
          break;
        case "geographic":
          setGeographicData(data.data.chartData || []);
          setGeographicSummary(data.data.summary || {});
          setChartKey((prev) => prev + 1); // Force chart re-render
          break;
        default:
          break;
      }
    } catch (err) {
      console.error(`Error fetching ${selectedMetric} analytics:`, err);
      setError(err.message);

      // Set fallback data
      const fallbackData =
        superAdminAnalyticsService.getFallbackData(selectedMetric);
      switch (selectedMetric) {
        case "overview":
          setPlatformOverviewData(fallbackData.chartData || []);
          setPlatformSummary(fallbackData.summary || {});
          break;
        case "users":
          setUserGrowthData(fallbackData.chartData || []);
          setUserGrowthSummary(fallbackData.summary || {});
          break;
        case "revenue":
          setRevenueBreakdownData(fallbackData.breakdownData || []);
          setHospitalPerformanceData(fallbackData.hospitalPerformance || []);
          setRevenueSummary(fallbackData.summary || {});
          break;
        case "performance":
          setDriverPerformanceData(fallbackData.driverPerformance || []);
          setSystemPerformanceData(fallbackData.systemMetrics || []);
          setPerformanceSummary(fallbackData.summary || {});
          break;
        case "geographic":
          setGeographicData(fallbackData.chartData || []);
          setGeographicSummary(fallbackData.summary || {});
          break;
        default:
          break;
      }
    } finally {
      setIsLoading(false);
    }
  }, [selectedMetric, timeRange]);

  // Load data on component mount and when dependencies change
  useEffect(() => {
    fetchAnalyticsData();
  }, [fetchAnalyticsData]);

  // Refresh data function
  const refreshData = useCallback(() => {
    setLastRefresh(new Date());
    fetchAnalyticsData();
  }, [fetchAnalyticsData]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat("en-IN").format(value);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "excellent":
        return "text-green-600";
      case "good":
        return "text-blue-600";
      case "warning":
        return "text-yellow-600";
      case "critical":
        return "text-red-600";
      default:
        return "text-gray-600";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "excellent":
        return <Shield className="w-4 h-4 text-green-600" />;
      case "good":
        return <Target className="w-4 h-4 text-blue-600" />;
      case "warning":
        return <Zap className="w-4 h-4 text-yellow-600" />;
      case "critical":
        return <Activity className="w-4 h-4 text-red-600" />;
      default:
        return <Activity className="w-4 h-4 text-gray-600" />;
    }
  };

  const handleMetricChange = (metric) => {
    setSelectedMetric(metric);
  };

  const handleTimeRangeChange = (range) => {
    setTimeRange(range);
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm text-red-800 font-medium">
              Failed to load analytics data
            </p>
            <p className="text-xs text-red-600 mt-1">{error}</p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={refreshData}
            className="text-red-600 border-red-300 hover:bg-red-50"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Retry
          </Button>
        </div>
      )}

      {/* Controls */}
      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-start sm:items-center justify-between">
        <div className="flex flex-wrap gap-2 w-full sm:w-auto">
          {[
            { key: "revenue", label: "Revenue", icon: DollarSign },
            { key: "performance", label: "Performance", icon: Zap },
            { key: "geographic", label: "Geographic", icon: MapPin },
          ].map((metric) => {
            const Icon = metric.icon;
            return (
              <Button
                key={metric.key}
                variant={selectedMetric === metric.key ? "default" : "outline"}
                size="sm"
                onClick={() => handleMetricChange(metric.key)}
                disabled={isLoading}
                className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm"
              >
                <Icon className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">{metric.label}</span>
                <span className="sm:hidden">{metric.label.split(" ")[0]}</span>
              </Button>
            );
          })}
        </div>

        <div className="flex gap-2 w-full sm:w-auto">
          {[
            { key: "1month", label: "1M" },
            { key: "3months", label: "3M" },
            { key: "6months", label: "6M" },
            { key: "1year", label: "1Y" },
          ].map((range) => (
            <Button
              key={range.key}
              variant={timeRange === range.key ? "default" : "outline"}
              size="sm"
              onClick={() => handleTimeRangeChange(range.key)}
              disabled={isLoading}
              className="text-xs sm:text-sm flex-1 sm:flex-none"
            >
              {range.label}
            </Button>
          ))}

          <Button
            variant="outline"
            size="sm"
            onClick={refreshData}
            disabled={isLoading}
            className="flex items-center gap-2"
          >
            <RefreshCw
              className={`w-4 h-4 ${isLoading ? "animate-spin" : ""}`}
            />
            <span className="hidden sm:inline">Refresh</span>
          </Button>
        </div>
      </div>

      {/* Last Updated Info */}
      <div className="text-xs text-gray-500 text-center">
        Last updated: {lastRefresh.toLocaleTimeString()}
      </div>

      {/* Overview Chart */}
      {selectedMetric === "overview" && (
        <Card>
          <CardHeader className="px-3 sm:px-6 pt-3 sm:pt-6">
            <CardTitle className="flex items-center gap-2 text-sm sm:text-base">
              <Activity className="w-4 h-4 sm:w-5 sm:h-5" />
              Platform Overview
            </CardTitle>
          </CardHeader>
          <CardContent className="px-3 sm:px-6 pb-3 sm:pb-6">
            <ResponsiveContainer
              width="100%"
              height={300}
              key={`platform-overview-${chartKey}`}
            >
              <ComposedChart
                data={platformOverviewData}
                key={`composed-${chartKey}`}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis
                  dataKey="month"
                  stroke="#6B7280"
                  fontSize={10}
                  tick={{ fontSize: 10 }}
                />
                <YAxis
                  yAxisId="left"
                  stroke="#6B7280"
                  fontSize={10}
                  tick={{ fontSize: 10 }}
                  tickFormatter={(value) => formatNumber(value)}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  stroke="#6B7280"
                  fontSize={10}
                  tick={{ fontSize: 10 }}
                  tickFormatter={(value) => formatCurrency(value)}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "white",
                    border: "1px solid #E5E7EB",
                    borderRadius: "8px",
                    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                    fontSize: "12px",
                  }}
                  formatter={(value, name) => {
                    // Format the value based on the data key
                    let formattedValue;
                    let displayName;

                    switch (name) {
                      case "users":
                        formattedValue = formatNumber(value);
                        displayName = "Users";
                        break;
                      case "revenue":
                        formattedValue = formatCurrency(value);
                        displayName = "Revenue";
                        break;
                      case "trips":
                        formattedValue = formatNumber(value);
                        displayName = "Trips";
                        break;
                      case "uptime":
                        formattedValue = `${value}%`;
                        displayName = "Uptime";
                        break;
                      default:
                        formattedValue = formatNumber(value);
                        displayName = name;
                    }

                    return [formattedValue, displayName];
                  }}
                />
                <Legend />
                <Bar
                  yAxisId="left"
                  dataKey="users"
                  fill="#3B82F6"
                  name="Users"
                />
                <Bar
                  yAxisId="right"
                  dataKey="revenue"
                  fill="#10B981"
                  name="Revenue"
                />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="trips"
                  stroke="#8B5CF6"
                  strokeWidth={3}
                  name="Trips"
                />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="uptime"
                  stroke="#F59E0B"
                  strokeWidth={3}
                  name="Uptime %"
                />
              </ComposedChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* User Growth Chart */}
      {selectedMetric === "users" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
          <Card>
            <CardHeader className="px-3 sm:px-6 pt-3 sm:pt-6">
              <CardTitle className="flex items-center gap-2 text-sm sm:text-base">
                <Users className="w-4 h-4 sm:w-5 sm:h-5" />
                User Growth Trends
              </CardTitle>
            </CardHeader>
            <CardContent className="px-3 sm:px-6 pb-3 sm:pb-6">
              {userGrowthData && userGrowthData.length > 0 ? (
                <ResponsiveContainer
                  width="100%"
                  height={250}
                  key={`user-growth-${chartKey}`}
                >
                  <AreaChart data={userGrowthData} key={`chart-${chartKey}`}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis
                      dataKey="date"
                      stroke="#6B7280"
                      fontSize={12}
                      tickFormatter={(value) =>
                        new Date(value).toLocaleDateString("en-IN", {
                          month: "short",
                          day: "numeric",
                        })
                      }
                    />
                    <YAxis
                      stroke="#6B7280"
                      fontSize={12}
                      tickFormatter={(value) => formatNumber(value)}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "white",
                        border: "1px solid #E5E7EB",
                        borderRadius: "8px",
                        boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                      }}
                      labelFormatter={(value) =>
                        new Date(value).toLocaleDateString("en-IN")
                      }
                      formatter={(value, name) => {
                        // Format the value based on the data key
                        let formattedValue;
                        let displayName;

                        switch (name) {
                          case "newUsers":
                            formattedValue = formatNumber(value);
                            displayName = "New Users";
                            break;
                          case "activeUsers":
                            formattedValue = formatNumber(value);
                            displayName = "Active Users";
                            break;
                          case "churnRate":
                            formattedValue = `${value}%`;
                            displayName = "Churn Rate";
                            break;
                          default:
                            formattedValue = formatNumber(value);
                            displayName = name;
                        }

                        return [formattedValue, displayName];
                      }}
                    />
                    <Area
                      type="monotone"
                      dataKey="activeUsers"
                      stroke="#3B82F6"
                      fill="#3B82F6"
                      fillOpacity={0.3}
                      name="Active Users"
                    />
                    <Area
                      type="monotone"
                      dataKey="newUsers"
                      stroke="#10B981"
                      fill="#10B981"
                      fillOpacity={0.3}
                      name="New Users"
                    />
                    <Legend />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[250px] flex flex-col items-center justify-center text-gray-500">
                  <Users className="w-12 h-12 mb-2 text-gray-400" />
                  <p className="text-sm">
                    No user growth data available for this period
                  </p>
                  <p className="text-xs mt-1">
                    Try selecting a different time range
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingDown className="w-5 h-5" />
                Churn Rate Analysis
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer
                width="100%"
                height={400}
                key={`user-growth-line-${chartKey}`}
              >
                <LineChart data={userGrowthData} key={`line-chart-${chartKey}`}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis
                    dataKey="date"
                    stroke="#6B7280"
                    fontSize={12}
                    tickFormatter={(value) =>
                      new Date(value).toLocaleDateString("en-IN", {
                        month: "short",
                        day: "numeric",
                      })
                    }
                  />
                  <YAxis
                    stroke="#6B7280"
                    fontSize={12}
                    label={{
                      value: "Churn Rate %",
                      angle: -90,
                      position: "insideLeft",
                    }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "white",
                      border: "1px solid #E5E7EB",
                      borderRadius: "8px",
                      boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                    }}
                    formatter={(value) => [`${value}%`, "Churn Rate"]}
                  />
                  <Line
                    type="monotone"
                    dataKey="churnRate"
                    stroke="#EF4444"
                    strokeWidth={3}
                    dot={{ fill: "#EF4444", strokeWidth: 2, r: 4 }}
                    name="Churn Rate"
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Revenue Chart */}
      {selectedMetric === "revenue" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="w-5 h-5" />
                Revenue Breakdown
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer
                width="100%"
                height={400}
                key={`revenue-pie-${chartKey}`}
              >
                <PieChart key={`pie-${chartKey}`}>
                  <Pie
                    data={revenueBreakdownData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percentage }) => `${name} ${percentage}%`}
                    outerRadius={120}
                    fill="#8884d8"
                    dataKey="revenue"
                  >
                    {revenueBreakdownData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "white",
                      border: "1px solid #E5E7EB",
                      borderRadius: "8px",
                      boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                    }}
                    formatter={(value) => [formatCurrency(value), "Revenue"]}
                  />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                Revenue vs Trips Correlation
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer
                width="100%"
                height={400}
                key={`hospital-scatter-${chartKey}`}
              >
                <ScatterChart
                  data={hospitalPerformanceData}
                  key={`scatter-${chartKey}`}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis
                    dataKey="trips"
                    stroke="#6B7280"
                    fontSize={12}
                    name="Trips"
                  />
                  <YAxis
                    dataKey="revenue"
                    stroke="#6B7280"
                    fontSize={12}
                    tickFormatter={(value) => formatCurrency(value)}
                    name="Revenue"
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "white",
                      border: "1px solid #E5E7EB",
                      borderRadius: "8px",
                      boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                    }}
                    formatter={(value, name) => {
                      // Format the value based on the data key
                      let formattedValue;
                      let displayName;

                      switch (name) {
                        case "revenue":
                          formattedValue = formatCurrency(value);
                          displayName = "Revenue";
                          break;
                        case "trips":
                          formattedValue = formatNumber(value);
                          displayName = "Trips";
                          break;
                        default:
                          formattedValue = formatNumber(value);
                          displayName = name;
                      }

                      return [formattedValue, displayName];
                    }}
                  />
                  <Scatter
                    dataKey="revenue"
                    fill="#8B5CF6"
                    name="Revenue vs Trips"
                  />
                </ScatterChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Performance Chart */}
      {selectedMetric === "performance" && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap className="w-5 h-5" />
                System Performance Metrics
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                {systemPerformanceData.map((metric, index) => (
                  <div key={index} className="p-4 border rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-600">
                        {metric.metric}
                      </span>
                      {getStatusIcon(metric.status)}
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-2xl font-bold">
                        {metric.value}
                        {metric.unit}
                      </span>
                      <span className="text-sm text-gray-500">
                        Target: {metric.target}
                        {metric.unit}
                      </span>
                    </div>
                    <div
                      className={`text-xs mt-1 ${getStatusColor(
                        metric.status
                      )}`}
                    >
                      {metric.status === "excellent" && "✓ Excellent"}
                      {metric.status === "good" && "✓ Good"}
                      {metric.status === "warning" && "⚠ Needs Attention"}
                      {metric.status === "critical" && "⚠ Critical"}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Driver Performance
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer
                  width="100%"
                  height={400}
                  key={`driver-performance-${chartKey}`}
                >
                  <BarChart
                    data={driverPerformanceData}
                    layout="horizontal"
                    key={`bar-h-${chartKey}`}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis
                      type="number"
                      stroke="#6B7280"
                      fontSize={12}
                      tickFormatter={(value) => formatNumber(value)}
                    />
                    <YAxis
                      type="category"
                      dataKey="driver"
                      stroke="#6B7280"
                      fontSize={12}
                      width={100}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "white",
                        border: "1px solid #E5E7EB",
                        borderRadius: "8px",
                        boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                      }}
                      formatter={(value, name) => {
                        // Format the value based on the data key
                        let formattedValue;
                        let displayName;

                        switch (name) {
                          case "earnings":
                            formattedValue = formatCurrency(value);
                            displayName = "Earnings";
                            break;
                          case "trips":
                            formattedValue = formatNumber(value);
                            displayName = "Trips";
                            break;
                          default:
                            formattedValue = formatNumber(value);
                            displayName = name;
                        }

                        return [formattedValue, displayName];
                      }}
                    />
                    <Bar dataKey="trips" fill="#8B5CF6" name="Trips" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  Response Time Analysis
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer
                  width="100%"
                  height={400}
                  key={`driver-bar-${chartKey}`}
                >
                  <BarChart
                    data={driverPerformanceData}
                    key={`bar-v-${chartKey}`}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis
                      dataKey="driver"
                      stroke="#6B7280"
                      fontSize={12}
                      angle={-45}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis
                      stroke="#6B7280"
                      fontSize={12}
                      label={{
                        value: "Response Time (mins)",
                        angle: -90,
                        position: "insideLeft",
                      }}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "white",
                        border: "1px solid #E5E7EB",
                        borderRadius: "8px",
                        boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                      }}
                      formatter={(value) => [`${value} mins`, "Response Time"]}
                    />
                    <Bar
                      dataKey="responseTime"
                      fill="#10B981"
                      name="Response Time"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Geographic Chart */}
      {selectedMetric === "geographic" && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5" />
              Geographic Performance Analysis
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer
              width="100%"
              height={500}
              key={`geographic-${chartKey}`}
            >
              <ComposedChart
                data={geographicData}
                key={`geo-composed-${chartKey}`}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="city" stroke="#6B7280" fontSize={12} />
                <YAxis
                  yAxisId="left"
                  stroke="#6B7280"
                  fontSize={12}
                  tickFormatter={(value) => formatNumber(value)}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  stroke="#6B7280"
                  fontSize={12}
                  label={{
                    value: "Response Time (mins)",
                    angle: 90,
                    position: "insideRight",
                  }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "white",
                    border: "1px solid #E5E7EB",
                    borderRadius: "8px",
                    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                  }}
                  formatter={(value, name) => {
                    // Format the value based on the data key
                    let formattedValue;
                    let displayName;

                    switch (name) {
                      case "trips":
                        formattedValue = formatNumber(value);
                        displayName = "Trips";
                        break;
                      case "revenue":
                        formattedValue = formatCurrency(value);
                        displayName = "Revenue";
                        break;
                      case "avgResponse":
                        formattedValue = `${value} mins`;
                        displayName = "Response Time";
                        break;
                      case "hospitals":
                        formattedValue = formatNumber(value);
                        displayName = "Hospitals";
                        break;
                      default:
                        formattedValue = formatNumber(value);
                        displayName = name;
                    }

                    return [formattedValue, displayName];
                  }}
                />
                <Legend />
                <Bar
                  yAxisId="left"
                  dataKey="trips"
                  fill="#3B82F6"
                  name="Trips"
                />
                <Bar
                  yAxisId="left"
                  dataKey="revenue"
                  fill="#10B981"
                  name="Revenue"
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="avgResponse"
                  stroke="#EF4444"
                  strokeWidth={3}
                  name="Avg Response Time"
                />
              </ComposedChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 flex items-center gap-4 shadow-lg">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            <span className="text-gray-700">Loading analytics data...</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminPlatformChart;
